import {
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import type { PagedResponse } from '@/shared/api/api-types';
import type { useTableSearchParams } from '@/shared/hooks/use-table-search-params';

/**
 * Wire a TanStack table to server-driven data and URL state.
 *
 * Everything that changes the result set — page, size, sort — is *manual*:
 * the table does not paginate or sort anything itself, it just reports intent,
 * which is written to the URL, which drives the query. The table renders
 * exactly the rows the server returned (§41).
 *
 * That matters at this scale: Orders is a table with millions of rows behind
 * it. Client-side sorting would mean downloading all of them to reorder a page
 * of 25.
 *
 * Column visibility is the one piece of genuinely local state here — it is a
 * per-user view preference that does not affect which rows match, so it stays
 * in React rather than cluttering the URL.
 */

const EMPTY: never[] = [];

interface UseDataTableOptions<TData> {
  data: PagedResponse<TData> | undefined;
  columns: ColumnDef<TData, unknown>[];
  /** The URL-state controller from `useTableSearchParams`. */
  tableState: ReturnType<typeof useTableSearchParams>;
  /** Stable row id. Defaults to the array index, which is wrong for selection. */
  getRowId?: (row: TData) => string;
  /** Columns hidden until the user shows them (a default view, still one click away). */
  initialColumnVisibility?: VisibilityState;
}

export function useDataTable<TData>({
  data,
  columns,
  tableState,
  getRowId,
  initialColumnVisibility,
}: UseDataTableOptions<TData>) {
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => initialColumnVisibility ?? {},
  );
  const [rowSelection, setRowSelection] = useState({});

  // TanStack compares `sorting` by reference; rebuilding it inline on every
  // render would loop.
  const sorting: SortingState = useMemo(
    () => (tableState.sort ? [{ id: tableState.sort, desc: tableState.direction === 'desc' }] : []),
    [tableState.sort, tableState.direction],
  );

  const pagination = useMemo(
    () => ({ pageIndex: tableState.page - 1, pageSize: tableState.pageSize }),
    [tableState.page, tableState.pageSize],
  );

  const table = useReactTable({
    // A fresh `?? []` each render would invalidate the row model every time.
    data: data?.items ?? EMPTY,
    columns,
    getCoreRowModel: getCoreRowModel(),

    // The server owns all three.
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,

    pageCount: data?.totalPages ?? -1,
    rowCount: data?.totalItems,

    state: { sorting, pagination, columnVisibility, rowSelection },

    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,

    // Sorting and pagination are not stored here — they are pushed to the URL,
    // and flow back in through `state` above. One owner per slice.
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const first = next[0];
      // Both keys go in one update. Writing them with two calls loses one of
      // them — see the note on `setSorting`.
      tableState.setSorting(first?.id ?? null, first ? (first.desc ? 'desc' : 'asc') : null);
    },

    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater(pagination) : updater;
      if (next.pageSize !== pagination.pageSize) {
        tableState.setPageSize(next.pageSize);
      } else {
        tableState.setPage(next.pageIndex + 1);
      }
    },

    ...(getRowId ? { getRowId } : {}),
  });

  return table;
}
